/**
 * Partners Listing Block
 * Handles category filtering, infinite scroll, lazy Google Maps with MarkerClusterer
 */

/* global google, markerClusterer */

const _boundMapButtons = new WeakSet();

const SCROLL_ROOT_MARGIN = '400px';
const MAP_ROOT_MARGIN = '200px';

class PartnersListing {
  constructor(container) {
    this.container = container;
    this.categoriesContainer = container.querySelector(
      '[data-partners-categories]',
    );
    this.listContainer = container.querySelector('[data-partners-list]');
    this.mapContainer = container.querySelector('[data-partners-map]');
    this.sentinel = container.querySelector('[data-partners-sentinel]');
    this.loadingIndicator = container.querySelector('[data-partners-loading]');

    this.map = null;
    this.markers = [];
    this.clusterer = null;
    this.infoWindow = null;

    // Filter mode: 'single' or 'multiple'
    this.filterMode = container.dataset.filterMode || 'single';

    // For single mode
    this.activeCategory = 'all';

    // For multiple mode - array of active categories
    this.activeCategories = [];

    // Pagination state
    this.currentPage = 1;
    this.perPage = parseInt(container.dataset.perPage) || 50;
    this.totalPartners = parseInt(container.dataset.total) || 0;
    this.hasMore = this.totalPartners > this.perPage;
    this.isLoading = false;
    this.allLoaded = !this.hasMore;

    // Block config for API calls
    this.apiEndpoint = window.theme?.api?.endpointW8 || '';
    this.displayMode = container.dataset.displayMode || 'all';
    this.clickAction = container.dataset.clickAction || 'website';
    this.showAllCategories = container.dataset.showAllCategories || 'first';
    this.selectedPartners = container.dataset.selectedPartners || '';

    // Cached DOM query for partner cards
    this._partnersCards = null;

    // Initialize default categories for multiple mode
    const defaultCategories = container.dataset.defaultCategories;
    if (this.filterMode === 'multiple' && defaultCategories) {
      this.activeCategories = defaultCategories
        .split(',')
        .filter((c) => c.trim());
    }

    // Pre-generate cached marker SVG data URIs
    this._markerIcons = {
      normal: this._buildMarkerDataURI(false),
      hovered: this._buildMarkerDataURI(true),
    };

    this.init();
  }

  get partnersCards() {
    if (!this._partnersCards) {
      this._partnersCards =
        this.container.querySelectorAll('[data-partner-id]');
    }
    return this._partnersCards;
  }

  invalidateCardsCache() {
    this._partnersCards = null;
  }

  init() {
    this.initCategoryFilters();
    this.initInfiniteScroll();
    this.initShowOnMapButtons();

    if (this.mapContainer) {
      this.observeMap();
    }
  }

  destroy() {
    this.scrollObserver?.disconnect();
    this._partnersCards = null;
  }

  // ─── Infinite Scroll ──────────────────────────────────────────────

  initInfiniteScroll() {
    if (!this.sentinel || !this.hasMore) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && this.hasMore && !this.isLoading) {
          this.loadMore();
        }
      },
      { rootMargin: SCROLL_ROOT_MARGIN },
    );
    this.scrollObserver.observe(this.sentinel);
  }

  _buildAPIParams(extra = {}) {
    const params = new URLSearchParams({
      per_page: this.perPage,
      display_mode: this.displayMode,
      show_all_categories: this.showAllCategories,
      click_action: this.clickAction,
      ...extra,
    });

    if (this.selectedPartners) {
      params.set('selected_partners', this.selectedPartners);
    }

    return params;
  }

  async loadMore() {
    if (this.isLoading || !this.hasMore || !this.apiEndpoint) return;

    this.isLoading = true;
    this.showLoading(true);

    try {
      const nextPage = this.currentPage + 1;
      const params = this._buildAPIParams({ page: nextPage });

      const url = new URL(`${this.apiEndpoint}/partners`);
      url.search = params.toString();

      const response = await fetch(url, {
        headers: {
          'Accept-Language':
            window.theme?.i18n?.locale_pll || document.documentElement.lang,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // 204 No Content or empty body — no more data.
      // Parse as text first to avoid json() throwing on empty body
      // (content-length header is unreliable with chunked encoding).
      const text = await response.text();

      if (response.status === 204 || text.length === 0) {
        this.hasMore = false;
        this.allLoaded = true;
        this.scrollObserver?.disconnect();
        return;
      }

      const data = JSON.parse(text);

      if (data.items?.length) {
        data.items.forEach((html) => {
          this.listContainer.insertAdjacentHTML('beforeend', html);
        });
      }

      this.currentPage = nextPage;
      this.hasMore = data.has_more;
      this.allLoaded = !this.hasMore;

      this.invalidateCardsCache();
      this.applyCurrentFilter();
      this.initShowOnMapButtons();

      if (!this.hasMore) {
        this.scrollObserver?.disconnect();
      }
    } catch (e) {
      // Silently fail — retry on next scroll
      if (import.meta.env.DEV)
        console.error('PartnersListing: loadMore failed', e);
    } finally {
      this.isLoading = false;
      this.showLoading(false);
    }
  }

  /**
   * Load ALL remaining partners in one bulk request.
   * Used when a filter is clicked before everything is loaded.
   */
  async loadAll() {
    if (this.allLoaded || this.isLoading || !this.apiEndpoint) return;

    this.isLoading = true;
    this.showLoading(true);

    try {
      const params = this._buildAPIParams({ all: true });

      const url = new URL(`${this.apiEndpoint}/partners`);
      url.search = params.toString();

      const response = await fetch(url, {
        headers: {
          'Accept-Language':
            window.theme?.i18n?.locale_pll || document.documentElement.lang,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      // 204 No Content or empty body — nothing to add.
      // Parse as text first to avoid json() throwing on empty body
      // (consistent with loadMore() approach).
      const text = await response.text();

      if (response.status !== 204 && text.length > 0) {
        const data = JSON.parse(text);

        if (data.items?.length) {
          data.items.forEach((html) => {
            this.listContainer.insertAdjacentHTML('beforeend', html);
          });
        }
      }

      this.hasMore = false;
      this.allLoaded = true;

      this.invalidateCardsCache();
      this.applyCurrentFilter();
      this.initShowOnMapButtons();

      this.scrollObserver?.disconnect();
    } catch (e) {
      // Silently fail — user can retry filter action
      if (import.meta.env.DEV)
        console.error('PartnersListing: loadAll failed', e);
    } finally {
      this.isLoading = false;
      this.showLoading(false);
    }
  }

  showLoading(show) {
    if (this.loadingIndicator) {
      this.loadingIndicator.classList.toggle('hidden', !show || !this.hasMore);
    }
  }

  // ─── Category Filters ─────────────────────────────────────────────

  initCategoryFilters() {
    if (!this.categoriesContainer) return;

    // Apply initial filtering for multiple mode with default categories
    if (this.filterMode === 'multiple' && this.activeCategories.length > 0) {
      this.filterByMultipleCategories();
    }

    const buttons =
      this.categoriesContainer.querySelectorAll('[data-category]');

    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        const category = button.dataset.category;

        if (this.filterMode === 'multiple') {
          this.toggleCategory(category, button);
        } else {
          this.filterByCategory(category);
          this.updateActiveButton(button);
        }

        // If not all partners loaded yet, debounce bulk-load to prevent rapid-click spam
        if (this.hasMore) {
          clearTimeout(this._loadAllTimer);
          this._loadAllTimer = setTimeout(() => this.loadAll(), 150);
        }
      });
    });
  }

  applyCurrentFilter() {
    if (this.filterMode === 'multiple') {
      if (this.activeCategories.length > 0) {
        this.filterByMultipleCategories();
      }
    } else if (this.activeCategory !== 'all') {
      this.filterByCategory(this.activeCategory);
    }
  }

  toggleCategory(category, button) {
    const index = this.activeCategories.indexOf(category);

    if (index > -1) {
      this.activeCategories.splice(index, 1);
      this.updateButtonState(button, false);
    } else {
      this.activeCategories.push(category);
      this.updateButtonState(button, true);
    }

    this.filterByMultipleCategories();
  }

  updateButtonState(button, isActive) {
    if (isActive) {
      button.classList.remove(
        'border-mist-darker',
        'text-steel-dark',
        'border-mist-lighter',
      );
      button.classList.add('border-blue-base', 'text-blue-base');
      button.dataset.active = 'true';
    } else {
      button.classList.add('border-mist-lighter', 'text-steel-dark');
      button.classList.remove('border-blue-base', 'text-blue-base');
      button.dataset.active = 'false';
    }
  }

  filterByMultipleCategories() {
    this.invalidateCardsCache();

    this.partnersCards.forEach((card) => {
      const categoriesArray = (card.dataset.partnerCategories || '').split(',');

      if (this.activeCategories.length === 0) {
        card.style.display = '';
        card.classList.remove('hidden');
      } else {
        const hasMatch = this.activeCategories.some((cat) =>
          categoriesArray.includes(cat),
        );
        card.style.display = hasMatch ? '' : 'none';
        card.classList.toggle('hidden', !hasMatch);
      }
    });

    this.updateMarkersForCurrentFilter();
  }

  filterByCategory(category) {
    this.activeCategory = category;
    this.invalidateCardsCache();

    this.partnersCards.forEach((card) => {
      const categoriesArray = (card.dataset.partnerCategories || '').split(',');

      if (category === 'all' || categoriesArray.includes(category)) {
        card.style.display = '';
        card.classList.remove('hidden');
      } else {
        card.style.display = 'none';
        card.classList.add('hidden');
      }
    });

    this.updateMarkersForCurrentFilter();
  }

  updateActiveButton(activeButton) {
    const buttons =
      this.categoriesContainer.querySelectorAll('[data-category]');

    buttons.forEach((button) => {
      if (button === activeButton) {
        button.classList.remove(
          'border-mist-darker',
          'text-steel-dark',
          'border-mist-lighter',
        );
        button.classList.add(
          'border-blue-base',
          'text-blue-base',
          'bg-mist-lighter',
        );
        button.dataset.active = 'true';
      } else {
        button.classList.add(
          'border-mist-darker',
          'text-steel-dark',
          'bg-mist-lighter',
          'border-mist-lighter',
        );
        button.classList.remove('border-blue-base', 'text-blue-base');
        button.dataset.active = 'false';
      }
    });
  }

  /**
   * Unified marker visibility update for both filter modes.
   */
  updateMarkersForCurrentFilter() {
    if (!this.map || !this.markers.length) return;

    const visibleMarkers = this.markers.filter((marker) => {
      const cats = marker._categories || [];
      if (this.filterMode === 'multiple') {
        return (
          this.activeCategories.length === 0 ||
          this.activeCategories.some((cat) => cats.includes(cat))
        );
      }
      return (
        this.activeCategory === 'all' || cats.includes(this.activeCategory)
      );
    });

    // Remove ALL markers from the map first
    this.markers.forEach((m) => m.setMap(null));

    if (this.clusterer) {
      // Destroy old clusterer — setMap(null) triggers onRemove() → reset()
      // which properly cleans up cluster overlay elements from the DOM.
      // clearMarkers() alone does NOT remove overlay elements, causing
      // ghost clusters that display "0" count.
      this.clusterer.setMap(null);
      this.clusterer = null;
    }

    if (
      typeof markerClusterer !== 'undefined' &&
      markerClusterer.MarkerClusterer &&
      visibleMarkers.length > 0
    ) {
      // Recreate clusterer with only the visible markers
      this.clusterer = new markerClusterer.MarkerClusterer({
        map: this.map,
        markers: visibleMarkers,
      });
    } else {
      // No clusterer available — toggle visibility directly via Set (O(1) lookup)
      const visibleSet = new Set(visibleMarkers);
      this.markers.forEach((m) =>
        m.setMap(visibleSet.has(m) ? this.map : null),
      );
    }
  }

  // ─── Google Maps (Lazy Loading + Lazy Data) ─────────────────────

  observeMap() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          observer.disconnect();
          this.loadMapAndData();
        }
      },
      { rootMargin: MAP_ROOT_MARGIN },
    );
    observer.observe(this.mapContainer);
  }

  async loadMapAndData() {
    try {
      // Load Google Maps API and partner map data in parallel
      const [, partners] = await Promise.all([
        this.loadGoogleMapsAPI(),
        this.fetchMapData(),
      ]);

      if (partners?.length) {
        await this.initMap(partners);
      }
    } catch (e) {
      // Map loading failed — placeholder stays visible
      if (import.meta.env.DEV)
        console.error('PartnersListing: map loading failed', e);
    }
  }

  async fetchMapData() {
    if (!this.apiEndpoint) return [];

    try {
      const params = new URLSearchParams({
        display_mode: this.displayMode,
      });

      if (this.selectedPartners) {
        params.set('selected_partners', this.selectedPartners);
      }

      const url = new URL(`${this.apiEndpoint}/partners/map`);
      url.search = params.toString();

      const response = await fetch(url, {
        headers: {
          'Accept-Language':
            window.theme?.i18n?.locale_pll || document.documentElement.lang,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    } catch (e) {
      // Network error — return empty array so map renders without markers
      if (import.meta.env.DEV)
        console.error('PartnersListing: fetchMapData failed', e);
      return [];
    }
  }

  loadGoogleMapsAPI() {
    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && typeof google.maps !== 'undefined') {
        resolve();
        return;
      }

      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        this.waitForGoogleMaps().then(resolve).catch(reject);
        return;
      }

      const apiKey = this.mapContainer.dataset.mapsApiKey;
      const language = this.mapContainer.dataset.mapsLanguage;
      const script = document.createElement('script');
      const mapsUrl = new URL('https://maps.googleapis.com/maps/api/js');
      mapsUrl.searchParams.set('key', apiKey);
      mapsUrl.searchParams.set('libraries', 'places');
      mapsUrl.searchParams.set('language', language);
      script.src = mapsUrl.href;
      script.async = true;
      script.defer = true;
      script.onload = () => this.waitForGoogleMaps().then(resolve);
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  waitForGoogleMaps(maxAttempts = 50) {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const check = () => {
        if (
          typeof google !== 'undefined' &&
          typeof google.maps !== 'undefined'
        ) {
          resolve();
        } else if (attempts >= maxAttempts) {
          reject(new Error('Google Maps API failed to load'));
        } else {
          attempts++;
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  async initMap(partners) {
    if (!partners.length) return;

    // Hide placeholder
    const placeholder = this.mapContainer.querySelector(
      '[data-map-placeholder]',
    );
    if (placeholder) {
      placeholder.style.display = 'none';
    }

    // Calculate bounds
    const bounds = new google.maps.LatLngBounds();
    partners.forEach((p) => {
      if (p.lat && p.lng) {
        bounds.extend({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) });
      }
    });

    // Create map
    this.map = new google.maps.Map(this.mapContainer, {
      zoom: 6,
      center: bounds.getCenter(),
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
      gestureHandling: 'cooperative',
      styles: this.getMapStyles(),
    });

    this.map.fitBounds(bounds);
    this.infoWindow = new google.maps.InfoWindow();

    // Create markers (using cached icon)
    const normalIcon = {
      url: this._markerIcons.normal,
      scaledSize: new google.maps.Size(16, 20),
      anchor: new google.maps.Point(8, 20),
    };

    this.markers = partners
      .filter((p) => p.lat && p.lng)
      .map((partner) => {
        const marker = new google.maps.Marker({
          position: {
            lat: parseFloat(partner.lat),
            lng: parseFloat(partner.lng),
          },
          // Don't set map here — updateMarkersForCurrentFilter() will add
          // markers to the map via MarkerClusterer (or directly as fallback).
          title: partner.name,
          icon: normalIcon,
        });

        // Store data on marker for filtering and info window
        marker._partnerId = partner.id;
        marker._categories = partner.categories || [];
        marker._data = partner;

        marker.addListener('mouseover', () => {
          marker.setIcon({
            url: this._markerIcons.hovered,
            scaledSize: new google.maps.Size(20, 25),
            anchor: new google.maps.Point(10, 25),
          });
        });

        marker.addListener('mouseout', () => {
          marker.setIcon(normalIcon);
        });

        marker.addListener('click', () => {
          this.showInfoWindow(marker, partner);
        });

        return marker;
      });

    // MarkerClusterer — group nearby markers
    this.initMarkerClusterer();

    // Apply current filter to markers
    this.updateMarkersForCurrentFilter();
  }

  initMarkerClusterer() {
    if (!this.markers.length || !this.map) return;

    // If markerClusterer is already loaded, nothing to do here —
    // updateMarkersForCurrentFilter() will create the clusterer
    // with the correct filtered subset of markers.
    if (
      typeof markerClusterer !== 'undefined' &&
      markerClusterer.MarkerClusterer
    ) {
      return;
    }

    // Fallback: dynamically load MarkerClusterer, then apply filter
    const script = document.createElement('script');
    script.src =
      'https://unpkg.com/@googlemaps/markerclusterer@2.5.3/dist/index.min.js';
    script.async = true;
    script.onload = () => {
      this.updateMarkersForCurrentFilter();
    };
    document.head.appendChild(script);
  }

  showInfoWindow(marker, partner) {
    const wrapper = document.createElement('div');
    wrapper.className =
      'partner-info-window pb-2 px-2 min-w-[180px] max-w-[280px]';

    const title = document.createElement('h4');
    title.className = 'font-semibold text-sm leading-tight text-[#222E34]';
    title.textContent = partner.name;
    wrapper.appendChild(title);

    if (partner.address) {
      const addr = document.createElement('p');
      addr.className = 'text-xs text-[#4D5560] mt-1 leading-snug uppercase';
      addr.textContent = partner.address;
      wrapper.appendChild(addr);
    }

    if (partner.phone) {
      const phone = document.createElement('a');
      phone.href = 'tel:' + partner.phone.replace(/[^\d+]/g, '');
      phone.className =
        'text-xs text-[#4D5560] mt-1 block hover:text-blue-link';
      phone.textContent = partner.phone;
      wrapper.appendChild(phone);
    }

    if (partner.website && /^https?:\/\//i.test(partner.website)) {
      const link = document.createElement('a');
      link.href = partner.website;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.className = 'text-xs text-blue-link mt-1 block hover:underline';
      link.textContent = window.theme?.translations?.check || 'Check';
      wrapper.appendChild(link);
    }

    this.infoWindow.setContent(wrapper);
    this.infoWindow.open(this.map, marker);
  }

  // ─── Show on Map ──────────────────────────────────────────────────

  initShowOnMapButtons() {
    const buttons = this.container.querySelectorAll('[data-show-on-map]');

    buttons.forEach((button) => {
      if (_boundMapButtons.has(button)) return;
      _boundMapButtons.add(button);

      button.addEventListener('click', (e) => {
        e.preventDefault();
        const partnerId = button.dataset.showOnMap;
        this.showPartnerOnMap(partnerId);
      });
    });
  }

  showPartnerOnMap(partnerId) {
    if (!this.map || !this.markers.length) return;

    const marker = this.markers.find((m) => m._partnerId == partnerId);

    if (marker) {
      this.map.panTo(marker.getPosition());
      this.map.setZoom(14);
      this.showInfoWindow(marker, marker._data);
      this.mapContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // ─── Marker Icons (cached, no per-frame regeneration) ─────────────

  _buildMarkerDataURI(isHovered) {
    const lightColor = isHovered ? '#FF1A6C' : '#E3004F';
    const darkColor = isHovered ? '#E3004F' : '#B30944';

    const svg = `<svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clip-path="url(#clip0_13805_3022)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M7.56239 19.3143C7.95758 19.3143 15.0188 11.7697 15.0188 7.65163C15.0188 3.53355 11.6804 0.19519 7.56239 0.19519C3.44431 0.19519 0.105957 3.53355 0.105957 7.65163C0.105957 11.7697 7.16721 19.3143 7.56239 19.3143ZM7.5624 11.3811C9.65027 11.3811 11.3428 9.68851 11.3428 7.60064C11.3428 5.51277 9.65027 3.82022 7.5624 3.82022C5.47454 3.82022 3.78199 5.51277 3.78199 7.60064C3.78199 9.68851 5.47454 11.3811 7.5624 11.3811Z" fill="${lightColor}"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M0.105957 7.65163C0.105957 11.7697 7.16721 19.3143 7.56239 19.3143V11.3811C5.47453 11.381 3.78199 9.6885 3.78199 7.60064C3.78199 5.51278 5.47453 3.82023 7.56239 3.82022V0.19519C3.44431 0.19519 0.105957 3.53355 0.105957 7.65163Z" fill="${darkColor}"/>
      </g>
      <defs>
        <clipPath id="clip0_13805_3022">
          <rect width="16" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>`;
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
  }

  getMapStyles() {
    return [
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#e9e9e9' }, { lightness: 17 }],
      },
      {
        featureType: 'landscape',
        elementType: 'geometry',
        stylers: [{ color: '#f5f5f5' }, { lightness: 20 }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.fill',
        stylers: [{ color: '#ffffff' }, { lightness: 17 }],
      },
      {
        featureType: 'road.highway',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#ffffff' }, { lightness: 29 }, { weight: 0.2 }],
      },
      {
        featureType: 'road.arterial',
        elementType: 'geometry',
        stylers: [{ color: '#ffffff' }, { lightness: 18 }],
      },
      {
        featureType: 'road.local',
        elementType: 'geometry',
        stylers: [{ color: '#ffffff' }, { lightness: 16 }],
      },
      {
        featureType: 'poi',
        elementType: 'geometry',
        stylers: [{ color: '#f5f5f5' }, { lightness: 21 }],
      },
      {
        featureType: 'poi.park',
        elementType: 'geometry',
        stylers: [{ color: '#dedede' }, { lightness: 21 }],
      },
      {
        elementType: 'labels.text.stroke',
        stylers: [
          { visibility: 'on' },
          { color: '#ffffff' },
          { lightness: 16 },
        ],
      },
      {
        elementType: 'labels.text.fill',
        stylers: [{ saturation: 36 }, { color: '#333333' }, { lightness: 40 }],
      },
      {
        elementType: 'labels.icon',
        stylers: [{ visibility: 'off' }],
      },
      {
        featureType: 'transit',
        elementType: 'geometry',
        stylers: [{ color: '#f2f2f2' }, { lightness: 19 }],
      },
      {
        featureType: 'administrative',
        elementType: 'geometry.fill',
        stylers: [{ color: '#fefefe' }, { lightness: 20 }],
      },
      {
        featureType: 'administrative',
        elementType: 'geometry.stroke',
        stylers: [{ color: '#fefefe' }, { lightness: 17 }, { weight: 1.2 }],
      },
    ];
  }
}

// Initialize all partners listing blocks on page
const _partnersInstances = new WeakMap();

function initPartnersBlocks() {
  const blocks = document.querySelectorAll('[data-partners-block]');
  blocks.forEach((block) => {
    // Destroy previous instance if exists (prevents memory leak on facetwp-loaded)
    const prev = _partnersInstances.get(block);
    if (prev) {
      prev.destroy();
    }
    const instance = new PartnersListing(block);
    _partnersInstances.set(block, instance);
  });
}

document.addEventListener('DOMContentLoaded', initPartnersBlocks);

// Re-initialize after FacetWP or other AJAX operations (with cleanup)
document.addEventListener('facetwp-loaded', initPartnersBlocks);

export default PartnersListing;
